import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";

import { SharepointList, SharepointPermissions, SharepointSite } from "@core/models/sharepoint";

import { environment } from "environments/environment";
import { SilentRequest } from "@azure/msal-browser";

@Injectable()
export class SharepointIntegrationService {

    private _onAccessTokenChanged: BehaviorSubject<string>;

    /**
     * Constructor
     * @param {HttpClient} _http 
     * @param {MsalService} _msalService 
     * @param {MsalGuardConfiguration} msalGuardConfig 
     */
    constructor(private _http: HttpClient,
        private _msalService: MsalService,
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration){

        this._onAccessTokenChanged = new BehaviorSubject(null);
    }

    get onAccessTokenChanged(): Observable<any> {
        return this._onAccessTokenChanged.asObservable();
    }

    get accessToken(): string{
        return this._onAccessTokenChanged.value;
    }

    /**
     * Registrar el token para microsoft graph
     */
    register(): void{
        // Obtiene el access token para power bi
        this.getAccessToken()
        .then(resp => {
            this._onAccessTokenChanged.next(resp);
        })
        .catch(error => console.error(error));
    }

    /**
     * Función que sirve para obtener la información del sitio de sharepoint
     * @param {URL} site Indica la url del sitio
     * @returns {Observable<SharepointSite>} Devuelve un observable con la información del sitio de sharepoint
     */
    getSharepointSite(site: URL): Observable<SharepointSite>{

        const endpoint = `https://graph.microsoft.com/v1.0/sites/${site.hostname}:${site.pathname}`;

        // Crea el encapezado de la petición
        const headers = {
            'Authorization' : `Bearer ${this.accessToken}`
        };

        // Realiza la llamada al api
        return this._http.get<SharepointSite>(endpoint,{ headers: headers});
    }

    /**
     * Obtiene la lista de listas de un sitio de SharePoint
     * @param {SharepointSite} site Objeto de tipo Sharepoint Site
     * @returns {Observable<SharepointList>} Devuelve un observable con la lista de listas del sitio de sharepoint
     */
    getSharepointLists(site: SharepointSite): Observable<SharepointList> {
        const endpoint = `https://graph.microsoft.com/v1.0/sites/${site.id}/lists`;

        // Crea el encapezado de la petición
        const headers = {
            'Authorization' : `Bearer ${this.accessToken}`
        };

        // Realiza la llamada al api
        return this._http.get<SharepointList>(endpoint,{ headers: headers});
    }

    /**
     * Función que sirve para otorgar permisos de full control a la app de Beliv Sharepoint Manager
     * @param {SharepointSite} site Objeto de tipo Sharepoint Site
     * @returns {Observable<SharepointPermissions>} Devuelve un observable con los permisos asignados al sitio
     */
    grantPermissions(site: SharepointSite): Observable<SharepointPermissions> {

        const endpoint = `https://graph.microsoft.com/v1.0/sites/${site.id}/permissions`;

        // Crea el encapezado de la petición
        const headers = {
            'Authorization' : `Bearer ${this.accessToken}`
        };

        // Crea el cuerpo de la petición
        const body: SharepointPermissions = {
            roles: ['fullControl'],
            grantedToIdentities: [
                {
                    application: {
                        id: environment.sharepointConfig.id,
                        displayName: environment.sharepointConfig.name
                    }
                }
            ]
        };

        // Realiza la llamada al api
        return this._http.post<SharepointPermissions>(endpoint, body, { headers: headers});
    }

    /**
     * Obtiene el access token para power bi
     * @returns {Promise<string>} Devuelve el access token de power bi
     */
    private async getAccessToken(): Promise<string> {
        const siletRequest = {...this.msalGuardConfig.authRequest, forceRefresh: true} as SilentRequest;
        siletRequest.scopes = ['https://graph.microsoft.com/.default'];

        const result = await this._msalService.acquireTokenSilent(siletRequest).toPromise();

        return result.accessToken;
    }

}
